import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles, Dialog, DialogContent, Box, Button } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import H6 from '../../../../common/typographys/H6';
import Body2 from '../../../../common/typographys/Body2';
import SelectOutlined from '../../../../common/forms/SelectOutlined';
import AddPointFromPool from './AddPointFromPool';
import AddPointNewStandalone from './AddPointNewStandalone';
import { getThemes } from '../../../../../store/actions/themesActions';

const useStyles = makeStyles(() => ({
  content: {
    padding: '36px 48px!important',
    minWidth: '420px',
  },
  modeButtons: {
    display: 'flex',
    marginBottom: '20px',
  },
  modeButton: {
    marginRight: '10px',
  },
  themeSelect: {
    marginBottom: '20px',
  },
  boxButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '24px',
    gap: '8px'
  },
  error: {
    marginTop: '12px',
  },
}));

// O menu do react-select é renderizado num portal em document.body, que fica atrás do
// backdrop do Dialog (MUI) sem este override — mesmo ajuste já usado em MeetingDeliberation.js.
const themeSelectStyles = {
  menuPortal: base => ({
    ...base,
    zIndex: '9999!important',
  }),
};

const DialogAddMeetingPoint = ({ open, onClose, themes, typologyId, onSubmit }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const globalThemes = useSelector(state => state.themes?.themes || []);
  const [mode, setMode] = useState('pool');
  const [themeId, setThemeId] = useState(null);
  const [poolPoint, setPoolPoint] = useState(null);
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) dispatch(getThemes());
  }, [dispatch, open]);

  useEffect(() => {
    console.log('globalThemes', globalThemes);
  }, [globalThemes]);

  // Temas já na reunião + temas globais disponíveis (mesma fonte usada em "availableThemes"
  // na edição da reunião, CreateEditMeetingsProvider.js), para permitir associar um tema
  // existente ainda não usado nesta reunião em concreto.
  const themeOptions = useMemo(() => {
    const merged = new Map();
    themes.forEach(theme => merged.set(theme.id, theme.name));
    globalThemes.forEach(theme => merged.set(theme.id, theme.name));

    return [
      { value: null, label: 'Sem tema' },
      ...Array.from(merged.entries()).map(([value, label]) => ({ value, label })),
    ];
  }, [themes, globalThemes]);

  const resetAndClose = () => {
    setMode('pool');
    setThemeId(null);
    setPoolPoint(null);
    setName('');
    setSubmitting(false);
    setError(null);
    onClose();
  };

  const canSubmit = mode === 'pool' ? Boolean(poolPoint) : name.trim().length > 3;

  const handleSubmit = () => {
    setSubmitting(true);
    setError(null);

    const selectedTheme = themeOptions.find(option => option.value === themeId);

    onSubmit({
      mode,
      themeId,
      themeName: selectedTheme ? selectedTheme.label : null,
      poolPointId: poolPoint ? poolPoint.id : null,
      name,
    })
      .then(() => {
        resetAndClose();
      })
      .catch(() => {
        setSubmitting(false);
        setError('Não foi possível concluir a operação. Tente novamente.');
      });
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : resetAndClose} maxWidth="sm" fullWidth>
      <DialogContent className={classes.content}>
        <H6>Associar ponto à reunião</H6>
        <Box height="20px" />
        <Box className={classes.modeButtons}>
          <Button
            className={classes.modeButton}
            variant={mode === 'pool' ? 'contained' : 'outlined'}
            color="primary"
            disabled={submitting}
            onClick={() => setMode('pool')}
          >
            Associar proposta existente
          </Button>
          <Button
            variant={mode === 'new' ? 'contained' : 'outlined'}
            color="primary"
            disabled={submitting}
            onClick={() => setMode('new')}
          >
            Criar novo ponto
          </Button>
        </Box>
        <Box className={classes.themeSelect}>
          <SelectOutlined
            label="Tema"
            name="theme"
            isDisabled={submitting}
            options={themeOptions}
            otherStyles={themeSelectStyles}
            value={themeOptions.find(option => option.value === themeId) || themeOptions[0]}
            onChange={option => setThemeId(option.value)}
          />
        </Box>
        {mode === 'pool' ? (
          <AddPointFromPool
            typologyId={typologyId}
            selectedPointId={poolPoint ? poolPoint.id : null}
            onSelect={setPoolPoint}
          />
        ) : (
          <AddPointNewStandalone name={name} onChangeName={setName} />
        )}
        {error && (
          <Body2 error className={classes.error}>
            {error}
          </Body2>
        )}
        <Box className={classes.boxButtons}>
          <Button
            variant="contained"
            color="primary"
            disabled={submitting}
            onClick={resetAndClose}
          >
            Cancelar
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={submitting || !canSubmit}
            onClick={handleSubmit}
          >
            Associar
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

DialogAddMeetingPoint.defaultProps = {
  typologyId: null,
};

DialogAddMeetingPoint.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  themes: PropTypes.array.isRequired,
  typologyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSubmit: PropTypes.func.isRequired,
};

export default React.memo(DialogAddMeetingPoint);
